import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ConfirmDialog from "../components/ConfirmDialog";
import SBINModal from "../components/modals/SBINModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import {
  fetchBusinessSettings,
  updateBusinessSettings,
  listBusinessUsers,
  addBusinessUser,
  removeBusinessUser,
  listBusinessSalons,
  attachSalonToBusiness,
  detachSalonFromBusiness,
  repairSalonBusinessLinks,
  getBusinessMergePreview,
  mergeBusinesses,
} from "../api/adminBusinessApi";
import { selectSalon } from "../api/auth";
import { createBusiness, createSalonUnderBusiness } from "../api/businesses";
import {
  MODAL_TEXT,
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_PRIMARY,
  BTN_OUTLINE,
  TAB_BASE,
  TAB_ACTIVE,
  TAB_INACTIVE,
} from "../ui/modalStyles";

function resolveBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch (err) {
    return "America/New_York";
  }
}

function truncateText(value, maxLength = 140) {
  const text = String(value || "").trim();
  if (!text) return "No description";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}...`;
}

function summarizeRepairCounts(updated) {
  if (!updated || typeof updated !== "object") return "No changes needed.";
  const labels = {
    tickets: "tickets",
    ticket_refunds: "refunds",
    cash_drawers: "cash drawers",
    staff_time_off: "staff time off",
    daily_closing_reports: "daily closing reports",
    time_clock_entries: "time clock entries",
    report_jobs: "report jobs",
    google_drive_integrations: "drive integrations",
    oauth_state_tokens: "oauth tokens",
  };
  const parts = Object.entries(updated)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${Number(count)} ${labels[key] || key}`);
  if (parts.length === 0) return "No changes needed.";
  return parts.join(", ");
}

function dispatchSalonMovedEvent(payload = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("sbin:salon-moved", {
      detail: payload,
    })
  );
}

export default function AdminBusinessSettings() {
  const { token, role, user, logout, login } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [business, setBusiness] = useState(null);
  const [users, setUsers] = useState([]);
  const [salons, setSalons] = useState([]);
  const [detachSalon, setDetachSalon] = useState(null);
  const [repairSalon, setRepairSalon] = useState(null);
  const [repairSubmitting, setRepairSubmitting] = useState(false);
  const [detachBlockedModalOpen, setDetachBlockedModalOpen] = useState(false);
  const [attachSalonConfirmId, setAttachSalonConfirmId] = useState(null);
  const [isAddSalonModalOpen, setIsAddSalonModalOpen] = useState(false);
  const [salonBusinessChoice, setSalonBusinessChoice] = useState("same");
  const [newBusinessName, setNewBusinessName] = useState("");
  const [newBusinessDescription, setNewBusinessDescription] = useState("");
  const [isCreatingSalon, setIsCreatingSalon] = useState(false);
  const [createSalonError, setCreateSalonError] = useState("");
  const [showNewBusinessHelp, setShowNewBusinessHelp] = useState(false);
  const [showMergeHelp, setShowMergeHelp] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [newUserId, setNewUserId] = useState("");
  const [newUserRole, setNewUserRole] = useState("member");
  const [attachSalonId, setAttachSalonId] = useState("");
  const [mergeSourceBusinessId, setMergeSourceBusinessId] = useState("");
  const [mergeMode, setMergeMode] = useState("salons_only");
  const [mergePreview, setMergePreview] = useState(null);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isMergePreviewLoading, setIsMergePreviewLoading] = useState(false);
  const [isMergingBusinesses, setIsMergingBusinesses] = useState(false);
  const [mergeAcknowledged, setMergeAcknowledged] = useState(false);
  const [activeSection, setActiveSection] = useState("business");
  const [flash, setFlash] = useState(null);
  const businessSectionRef = useRef(null);
  const usersSectionRef = useRef(null);
  const salonsSectionRef = useRef(null);
  const flashTimerRef = useRef(null);
  const detachDisabled = salons.length <= 1;
  const canSuperAdmin = Boolean(user?.is_super_admin);
  const canAttachExistingSalon = canSuperAdmin;
  const currentBusinessId = Number(
    business?.id ??
    user?.business_id ??
    (typeof window !== "undefined"
      ? window.localStorage.getItem("business_id")
      : null)
  );
  const hasCurrentBusiness =
    Number.isFinite(currentBusinessId) && currentBusinessId > 0;
  const currentBusinessLabel = hasCurrentBusiness
    ? `${business?.code || "BIZ-XXXXX"} - ${business?.name || "Current Business"}`
    : "Current business unavailable";
  const detachMessage =
    "This will move the salon into a new business profile.\n" +
    "It will not delete the salon or its data.\n" +
    "This salon will no longer appear under this business, and business reports will change.\n" +
    "Anyone currently logged into this salon will need to log out and log back in.";

  useEffect(() => {
    if (!token) {
      navigate("/login?next=/admin/business-settings", { replace: true });
      return;
    }
    if (!Boolean(user?.is_super_admin)) {
      navigate("/", { replace: true });
    }
  }, [token, user?.is_super_admin, navigate]);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [settings, usersList, salonsList] = await Promise.all([
        fetchBusinessSettings(token),
        listBusinessUsers(token),
        listBusinessSalons(token),
      ]);
      setBusiness(settings);
      setUsers(usersList);
      setSalons(salonsList);
      setName(settings?.name || "");
      setDescription(settings?.description || "");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Failed to load settings";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
    };
  }, []);

  const showFlash = (type, text, durationMs) => {
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current);
    }
    setFlash({ type, text });
    flashTimerRef.current = setTimeout(() => {
      setFlash(null);
      flashTimerRef.current = null;
    }, durationMs);
  };

  const getSectionRef = (sectionKey) => {
    if (sectionKey === "business") return businessSectionRef;
    if (sectionKey === "users") return usersSectionRef;
    if (sectionKey === "salons") return salonsSectionRef;
    return null;
  };

  const handleSectionPillClick = (sectionKey) => {
    const sectionRef = getSectionRef(sectionKey);
    setActiveSection(sectionKey);
    sectionRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleBusinessSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError("");
    try {
      const updated = await updateBusinessSettings(
        { name: name.trim(), description: description || null },
        token
      );
      setBusiness(updated);
      setDescription(updated?.description || "");
      showFlash("success", "Saved successfully.", 2500);
    } catch (err) {
      showFlash("error", "Save failed. Please try again.", 4000);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserId) return;
    setError("");
    try {
      const created = await addBusinessUser(
        { user_id: Number(newUserId), role: newUserRole },
        token
      );
      setUsers((prev) => {
        const existing = prev.filter((u) => u.user_id !== created.user_id);
        return [...existing, created];
      });
      setNewUserId("");
      setNewUserRole("member");
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Failed to add user"
      );
    }
  };

  const handleRemoveUser = async (userId) => {
    setError("");
    try {
      await removeBusinessUser(userId, token);
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Failed to remove user"
      );
    }
  };

  const getAttachSalonErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail === "SUPERADMIN_ONLY") {
      return "Only Super Admin can attach an existing salon by ID.";
    }
    return detail || err?.message || "Failed to attach salon";
  };

  const handleAttachSalon = (e) => {
    e.preventDefault();
    if (!canAttachExistingSalon) return;
    const salonId = Number(attachSalonId);
    if (!Number.isFinite(salonId) || salonId <= 0) {
      setError("Enter a valid Salon ID.");
      return;
    }
    setError("");
    setAttachSalonConfirmId(salonId);
  };

  const handleConfirmAttachSalon = async () => {
    if (!attachSalonConfirmId) return;
    setError("");
    try {
      const result = await attachSalonToBusiness(
        { salon_id: attachSalonConfirmId },
        token
      );
      const salon = result?.salon || null;
      const migrationCounts = result?.migration_counts || {};
      if (!salon?.id) {
        throw new Error("Attach succeeded but salon payload is missing.");
      }
      setSalons((prev) => {
        const exists = prev.some((s) => s.id === salon.id);
        if (exists) {
          return prev.map((entry) => (entry.id === salon.id ? salon : entry));
        }
        return [...prev, salon];
      });
      setAttachSalonId("");
      if (user?.salon_id) {
        try {
          const refreshed = await selectSalon(Number(user.salon_id), token);
          if (refreshed?.access_token) {
            login(refreshed.access_token, refreshed);
          }
        } catch (err) {
          // Keep attach success even if session refresh fails.
        }
      }
      dispatchSalonMovedEvent({
        salon_id: salon.id,
        to_business_id: result?.to_business_id,
        migration_counts: migrationCounts,
      });
      showFlash(
        "success",
        `Salon moved. Data migrated successfully. ${summarizeRepairCounts(migrationCounts)}`,
        4500
      );
      await loadAll();
    } catch (err) {
      setError(getAttachSalonErrorMessage(err));
    } finally {
      setAttachSalonConfirmId(null);
    }
  };

  const handleCancelAttachSalon = () => {
    setAttachSalonConfirmId(null);
  };

  const handleConfirmDetach = async () => {
    if (!detachSalon?.id) return;
    setError("");
    try {
      const detached = await detachSalonFromBusiness(detachSalon.id, token);
      setSalons((prev) => prev.filter((s) => s.id !== detachSalon.id));
      dispatchSalonMovedEvent({
        salon_id: detachSalon.id,
        to_business_id: detached?.to_business_id,
        migration_counts: detached?.migration_counts || {},
      });
      setDetachSalon(null);
      showFlash(
        "success",
        `Salon moved. Data migrated successfully. ${summarizeRepairCounts(detached?.migration_counts)}`,
        5000
      );
      if (detachSalon.id === user?.salon_id) {
        await logout();
        navigate("/login?next=/admin/business-settings", { replace: true });
        return;
      }
      if (user?.salon_id) {
        try {
          const refreshed = await selectSalon(Number(user.salon_id), token);
          if (refreshed?.access_token) {
            login(refreshed.access_token, refreshed);
          }
        } catch (err) {
          // Keep detach success even if session refresh fails.
        }
      }
      await loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "CANNOT_DETACH_ONLY_SALON") {
        setDetachBlockedModalOpen(true);
        setError("");
      } else {
        setError(detail || err?.message || "Failed to detach salon");
      }
      setDetachSalon(null);
    }
  };

  const handleCancelDetach = () => {
    setDetachSalon(null);
  };

  const closeRepairModal = () => {
    if (repairSubmitting) return;
    setRepairSalon(null);
  };

  const handleConfirmRepairSalon = async () => {
    if (!repairSalon?.id) return;
    setError("");
    setRepairSubmitting(true);
    try {
      const payload =
        hasCurrentBusiness && canSuperAdmin
          ? { target_business_id: currentBusinessId }
          : {};
      const result = await repairSalonBusinessLinks(repairSalon.id, payload, token);
      showFlash(
        "success",
        `Repaired: ${summarizeRepairCounts(result?.updated)}`,
        5000
      );
      setRepairSalon(null);
      await loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to repair salon data";
      setError(detail);
      showFlash("error", detail, 4500);
    } finally {
      setRepairSubmitting(false);
    }
  };

  const getMergeErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail === "SUPERADMIN_ONLY") {
      return "Only Super Admin can merge businesses.";
    }
    if (detail === "SOURCE_AND_TARGET_MUST_DIFFER") {
      return "Source and preserved business must be different.";
    }
    if (detail === "BUSINESS_ALREADY_MERGED") {
      return "Source business is already merged/archived.";
    }
    if (detail === "SOURCE_BUSINESS_INACTIVE") {
      return "Source business is inactive and cannot be merged.";
    }
    if (detail === "TARGET_BUSINESS_INACTIVE") {
      return "Preserved business is inactive. Select an active business to preserve.";
    }
    if (detail === "BUSINESS_NOT_FOUND") {
      return "Business not found. Check the ID and try again.";
    }
    return detail || err?.message || "Unable to process business merge.";
  };

  const resetMergeFlow = () => {
    setMergePreview(null);
    setIsMergeModalOpen(false);
    setMergeAcknowledged(false);
    setIsMergingBusinesses(false);
  };

  const handlePreviewMerge = async (e) => {
    e.preventDefault();
    if (!canSuperAdmin) return;

    const sourceBusinessId = Number(mergeSourceBusinessId);
    if (!Number.isFinite(sourceBusinessId) || sourceBusinessId <= 0) {
      setError("Enter a valid Source Business ID.");
      return;
    }
    if (!hasCurrentBusiness) {
      setError("Current preserved business is unavailable.");
      return;
    }

    setError("");
    setIsMergePreviewLoading(true);
    try {
      const preview = await getBusinessMergePreview(
        {
          source_business_id: sourceBusinessId,
          target_business_id: currentBusinessId,
          mode: mergeMode,
        },
        token
      );
      setMergePreview(preview);
      setMergeAcknowledged(false);
      setIsMergeModalOpen(true);
    } catch (err) {
      setError(getMergeErrorMessage(err));
    } finally {
      setIsMergePreviewLoading(false);
    }
  };

  const handleCancelMergeModal = () => {
    if (isMergingBusinesses) return;
    resetMergeFlow();
  };

  const handleConfirmMergeBusinesses = async () => {
    if (!mergeAcknowledged || !mergePreview || isMergingBusinesses) return;

    setError("");
    setIsMergingBusinesses(true);
    try {
      await mergeBusinesses(
        {
          source_business_id: mergePreview?.source?.id,
          target_business_id: mergePreview?.target?.id,
          mode: mergeMode,
        },
        token
      );
      resetMergeFlow();
      setMergeSourceBusinessId("");
      showFlash("success", "Merged successfully. Source business archived.", 3500);
      await loadAll();
    } catch (err) {
      setError(getMergeErrorMessage(err));
    } finally {
      setIsMergingBusinesses(false);
    }
  };

  const sectionPills = [
    { key: "business", label: "Business" },
    { key: "users", label: "Users" },
    { key: "salons", label: "Salons" },
  ];

  const openAddSalonModal = () => {
    setCreateSalonError("");
    setSalonBusinessChoice("same");
    setNewBusinessName("");
    setNewBusinessDescription("");
    setShowNewBusinessHelp(false);
    setIsAddSalonModalOpen(true);
  };

  const closeAddSalonModal = () => {
    if (isCreatingSalon) return;
    setIsAddSalonModalOpen(false);
    setCreateSalonError("");
  };

  const handleCreateSalonAndStartSetup = async () => {
    if (!token) return;

    const usingCurrentBusiness = salonBusinessChoice === "same";
    if (usingCurrentBusiness && !hasCurrentBusiness) {
      setCreateSalonError("Current business context is missing. Please reload and try again.");
      return;
    }

    if (!usingCurrentBusiness && !newBusinessName.trim()) {
      setCreateSalonError("Business name is required.");
      return;
    }

    setIsCreatingSalon(true);
    setCreateSalonError("");
    try {
      let targetBusinessId = currentBusinessId;
      if (!usingCurrentBusiness) {
        const createdBusiness = await createBusiness(
          {
            name: newBusinessName.trim(),
            description: newBusinessDescription.trim() || null,
          },
          token
        );
        targetBusinessId = Number(createdBusiness?.id);
      }

      if (!Number.isFinite(targetBusinessId) || targetBusinessId <= 0) {
        throw new Error("Unable to resolve business for salon creation.");
      }

      const createdSalon = await createSalonUnderBusiness(
        targetBusinessId,
        {
          name: "New Salon",
          timezone: resolveBrowserTimezone(),
        },
        token
      );
      const createdSalonId = Number(createdSalon?.id);
      if (!Number.isFinite(createdSalonId) || createdSalonId <= 0) {
        throw new Error("Salon was created but no salon ID was returned.");
      }

      const selected = await selectSalon(createdSalonId, token);
      if (!selected?.access_token) {
        throw new Error("Unable to switch into the new salon.");
      }

      login(selected.access_token, selected);
      setIsAddSalonModalOpen(false);
      navigate("/setup?step=1", { replace: true });
    } catch (err) {
      setCreateSalonError(
        err?.response?.data?.detail || err?.message || "Unable to create salon."
      );
    } finally {
      setIsCreatingSalon(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  if (loading) {
    return (
      <div className="manageSectionShell min-h-screen bg-slate-100">
        <div className="manageSectionWide manage-section-wide manage-section-compact">
          <div className="rounded-3xl bg-white shadow-2xl overflow-hidden border border-slate-200">
            <div className="bg-rose-500 px-6 py-4 relative">
              <h1 className="text-xl md:text-2xl font-semibold text-white">
                Business Settings
              </h1>
              <p className="text-sm md:text-base text-white/80">
                Manage your business profile, salons, and user access.
              </p>
              <div
                className="absolute right-4 top-3 z-50"
                onClick={handleClose}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <ModalCloseX onClick={handleClose} />
              </div>
            </div>
            <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
              <p className="text-slate-600">Loading business settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manageSectionShell min-h-screen bg-slate-100">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden border border-slate-200">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Business Settings
            </h1>
            <p className="text-sm md:text-base text-white/80">
              Manage your business profile, salons, and user access.
            </p>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div className="flex flex-wrap items-center gap-2">
              {sectionPills.map((pill) => (
                <button
                  key={pill.key}
                  type="button"
                  onClick={() => handleSectionPillClick(pill.key)}
                  className={`${TAB_BASE} ${activeSection === pill.key
                      ? TAB_ACTIVE
                      : `bg-white ${TAB_INACTIVE}`
                    }`}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}

            <div
              ref={businessSectionRef}
              data-section="business"
              className="space-y-4 scroll-mt-6"
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h2 className="text-base font-semibold text-slate-900">
                  Business vs Salon Names
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Business is your legal owner/parent company. Salon is your public
                  brand/location name.
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">
                  <li>Business: 123456 Ontario Inc. or 1234567 Canada Inc.</li>
                  <li>Salon: Regal Nails Trainyards</li>
                </ul>
              </div>

              {business && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
                  <div className="space-y-1">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                      Business
                    </h2>
                    <p className="text-sm text-slate-600">
                      Code: {business.code} - ID: {business.id}
                    </p>
                  </div>

                  <form className="mt-5 space-y-4" onSubmit={handleBusinessSave}>
                    <div className="space-y-2">
                      <label className={LABEL_TEXT}>
                        <span className="inline-flex items-center gap-2">
                          <span>Business Legal Name</span>
                          <InfoTooltip
                            label="Business Legal Name (what is this?)"
                            text={
                              "This is the legal name of the company that owns one or more salons.\n" +
                              "Examples: '123456 Ontario Inc.', 'Regal Nails Holdings Ltd.'\n" +
                              "Your salons can have different operating/brand names."
                            }
                          />
                        </span>
                      </label>
                      <input
                        type="text"
                        className={`${FIELD_CLASS} w-full`}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                      <p className="text-xs text-slate-500">
                        Tip: Use your registered legal company name. You&apos;ll set
                        each salon&apos;s public-facing name inside the Salon Setup
                        Wizard.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className={LABEL_TEXT}>Description</label>
                      <textarea
                        className={`${FIELD_CLASS} h-auto min-h-[120px] w-full py-3`}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <button type="submit" className={BTN_PRIMARY}>
                      Save
                    </button>
                  </form>
                </div>
              )}

              {canSuperAdmin && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 space-y-4">
                  <div className="space-y-1">
                    <h3 className="text-lg md:text-xl font-semibold text-slate-900">
                      Super Admin: Merge Businesses
                    </h3>
                    <p className="text-sm text-slate-600">
                      Move salons (and optionally user access) from one business into another.
                      The preserved business remains unchanged.
                    </p>
                  </div>

                  <form className="space-y-4" onSubmit={handlePreviewMerge}>
                    <div className="space-y-2">
                      <label className={LABEL_TEXT}>
                        <span className="inline-flex items-center gap-2">
                          <span>Source Business ID</span>
                          <InfoTooltip
                            label="Source Business ID"
                            text="This is the business that will be merged and then archived. Its salons (and optionally users) will move into the preserved business."
                          />
                        </span>
                      </label>
                      <input
                        type="number"
                        className={`${FIELD_CLASS} w-full`}
                        placeholder="Enter Source Business ID"
                        value={mergeSourceBusinessId}
                        onChange={(e) => setMergeSourceBusinessId(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className={LABEL_TEXT}>
                        <span className="inline-flex items-center gap-2">
                          <span>Merge Mode</span>
                          <InfoTooltip
                            label="Merge Mode"
                            text="Salons only: moves locations. Salons + user access: also copies business user access into the preserved business (without downgrading existing roles)."
                          />
                        </span>
                      </label>
                      <select
                        className={`${FIELD_CLASS} w-full`}
                        value={mergeMode}
                        onChange={(e) => setMergeMode(e.target.value)}
                      >
                        <option value="salons_only">Move salons only</option>
                        <option value="salons_and_users">Move salons + user access</option>
                      </select>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="submit"
                        className={BTN_PRIMARY}
                        disabled={isMergePreviewLoading || !hasCurrentBusiness}
                      >
                        {isMergePreviewLoading ? "Previewing..." : "Preview Merge"}
                      </button>
                      <InfoTooltip
                        label="Preview Merge"
                        text="Preview shows both businesses side-by-side and summarizes what will move before you confirm."
                      />
                      {!hasCurrentBusiness && (
                        <p className="text-xs text-rose-600">
                          Preserved business context is unavailable. Reload and try again.
                        </p>
                      )}
                    </div>
                  </form>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <button
                      type="button"
                      onClick={() => setShowMergeHelp((prev) => !prev)}
                      className="text-sm font-semibold text-slate-700 underline underline-offset-2"
                    >
                      How to merge businesses
                    </button>
                    {showMergeHelp && (
                      <div className="mt-3 space-y-3 text-sm text-slate-600">
                        <ol className="list-decimal pl-5 space-y-1">
                          <li>
                            Open the preserved business (the one you want to keep) in Business
                            Settings.
                          </li>
                          <li>Enter the Source Business ID (the one to merge).</li>
                          <li>Choose what to move: salons only, or salons + user access.</li>
                          <li>Click Preview Merge.</li>
                          <li>In the confirmation modal, review LEFT vs RIGHT carefully.</li>
                          <li>Check the acknowledgement box and click Merge.</li>
                        </ol>
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-slate-900">When to use this</h4>
                          <p>
                            Use this if a client accidentally created duplicate businesses, or if
                            two businesses should be consolidated under one preserved business.
                          </p>
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-slate-900">What happens after</h4>
                          <p>
                            The source business is archived for safety (not deleted). Salons and
                            optional user access are moved into the preserved business. Reporting
                            and access will follow the preserved business.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                ref={usersSectionRef}
                data-section="users"
                className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 space-y-4 scroll-mt-6"
              >
                <div>
                  <h3 className="text-lg md:text-xl font-semibold text-slate-900">
                    Users
                  </h3>
                  <p className="text-sm text-slate-600">
                    Manage who can access this business.
                  </p>
                </div>

                <form className="space-y-3" onSubmit={handleAddUser}>
                  <div className="grid grid-cols-1 gap-3">
                    <input
                      type="number"
                      className={`${FIELD_CLASS} w-full`}
                      placeholder="User ID"
                      value={newUserId}
                      onChange={(e) => setNewUserId(e.target.value)}
                      required
                    />
                    <select
                      className={`${FIELD_CLASS} w-full`}
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </select>
                    <button type="submit" className={BTN_PRIMARY}>
                      Add
                    </button>
                  </div>
                </form>

                <ul className="divide-y divide-slate-200">
                  {users.map((u) => (
                    <li
                      key={u.user_id}
                      className="py-3 flex items-center justify-between gap-3"
                    >
                      <div>
                        <div className="font-medium text-slate-900">
                          {u.full_name || u.email || `User #${u.user_id}`}
                        </div>
                        <div className="text-xs text-slate-500">
                          {u.email || "No email"} - {u.role}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-sm font-semibold text-rose-600 hover:text-rose-700"
                        onClick={() => handleRemoveUser(u.user_id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                  {users.length === 0 && (
                    <li className="py-3 text-sm text-slate-500">No users linked yet.</li>
                  )}
                </ul>
              </div>

              <div
                ref={salonsSectionRef}
                data-section="salons"
                className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 space-y-4 scroll-mt-6"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-slate-900">
                      Salons
                    </h3>
                    <p className="text-sm text-slate-600">
                      Salons attached to this business.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openAddSalonModal}
                    className={BTN_PRIMARY}
                  >
                    + Add another salon
                  </button>
                </div>

                {canAttachExistingSalon ? (
                  <form className="space-y-3" onSubmit={handleAttachSalon}>
                    <input
                      type="number"
                      className={`${FIELD_CLASS} w-full`}
                      placeholder="Salon ID"
                      value={attachSalonId}
                      onChange={(e) => setAttachSalonId(e.target.value)}
                    />
                    <p className="text-xs text-rose-600">
                      Super Admin only: Attaching moves a salon into this business and changes
                      reporting and access.
                    </p>
                    <button type="submit" className={BTN_PRIMARY}>
                      Attach
                    </button>
                  </form>
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Adding another salon
                    </h4>
                    <p className="text-sm text-slate-600">
                      To add a new location, use &apos;Add another salon&apos;.
                    </p>
                    <p className="text-sm text-slate-600">
                      For security, only Super Admin can attach an existing salon by ID.
                    </p>
                  </div>
                )}

                <ul className="divide-y divide-slate-200">
                  {salons.map((s) => (
                    <li key={s.id} className="py-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-slate-900">{s.name}</div>
                        <div className="text-xs text-slate-500">
                          {s.city || "Location TBD"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canSuperAdmin && (
                          <button
                            type="button"
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                            onClick={() => setRepairSalon(s)}
                          >
                            Repair Salon Data
                          </button>
                        )}
                        {detachDisabled ? (
                          <span className="text-sm text-slate-400 cursor-not-allowed">
                            Detach
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-sm font-semibold text-rose-600 hover:text-rose-700"
                            onClick={() => setDetachSalon(s)}
                          >
                            Detach
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                  {salons.length === 0 && (
                    <li className="py-3 text-sm text-slate-500">
                      No salons attached to this business.
                    </li>
                  )}
                </ul>

                {salons.length === 0 && (
                  <button
                    type="button"
                    onClick={openAddSalonModal}
                    className={`${BTN_PRIMARY} w-full`}
                  >
                    Create first salon & start setup
                  </button>
                )}
                {detachDisabled && salons.length === 1 && (
                  <p className="text-xs text-slate-500">
                    Detach is disabled because this business has only one salon.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SBINModal
        isOpen={isMergeModalOpen}
        onClose={handleCancelMergeModal}
        title="Merge Businesses"
        subtitle={
          <div className="flex flex-wrap items-center gap-2">
            <span>Left will be merged into Right. Right business info stays the same.</span>
            <InfoTooltip
              label="Merge behavior"
              text="Only salons/users move. The preserved business's legal name and details remain unchanged."
            />
          </div>
        }
        size="xxl"
        fitContent={true}
        closeOnBackdrop={!isMergingBusinesses}
        closeOnEscape={!isMergingBusinesses}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleCancelMergeModal}
              disabled={isMergingBusinesses}
              className={BTN_OUTLINE}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmMergeBusinesses}
              disabled={!mergeAcknowledged || isMergingBusinesses}
              className={BTN_PRIMARY}
            >
              {isMergingBusinesses ? "Merging..." : "Merge into preserved business"}
            </button>
          </div>
        }
      >
        {mergePreview && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 space-y-3">
                <div className="inline-flex items-center rounded-full border border-rose-300 bg-white px-3 py-1 text-xs font-semibold text-rose-700">
                  MERGE (ARCHIVE)
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-900">
                    {mergePreview?.source?.legal_name || "Unknown source business"}
                  </p>
                  <p className="text-xs text-slate-600">
                    {mergePreview?.source?.code || "N/A"} - ID: {mergePreview?.source?.id || "N/A"}
                  </p>
                </div>
                <p className="text-sm text-slate-600">
                  {truncateText(mergePreview?.source?.description)}
                </p>
                <div className="space-y-1 text-sm text-slate-700">
                  <p>Salons currently: {mergePreview?.source?.salon_count ?? 0}</p>
                  <p>Users currently: {mergePreview?.source?.user_count ?? 0}</p>
                </div>
                <p className="text-sm font-semibold text-rose-700">
                  This business will be archived after merge.
                </p>
              </div>

              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                <div className="inline-flex items-center rounded-full border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                  PRESERVED
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-900">
                    {mergePreview?.target?.legal_name || "Unknown target business"}
                  </p>
                  <p className="text-xs text-slate-600">
                    {mergePreview?.target?.code || "N/A"} - ID: {mergePreview?.target?.id || "N/A"}
                  </p>
                </div>
                <p className="text-sm text-slate-600">
                  {truncateText(mergePreview?.target?.description)}
                </p>
                <div className="space-y-1 text-sm text-slate-700">
                  <p>Salons currently: {mergePreview?.target?.salon_count ?? 0}</p>
                  <p>Users currently: {mergePreview?.target?.user_count ?? 0}</p>
                </div>
                <p className="text-sm font-semibold text-emerald-700">
                  This business remains active. Info stays unchanged.
                </p>
              </div>
            </div>

            <p className="text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
              LEFT -&gt; RIGHT
            </p>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <h4 className="text-base font-semibold text-slate-900">Move Summary</h4>
              <div className="space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2">
                  <span>
                    Salons moving to preserved business: {mergePreview?.to_move?.salons ?? 0}
                  </span>
                  <InfoTooltip
                    label="Salons moving"
                    text="All salons under the source business will be reassigned to the preserved business."
                  />
                </div>
                {mergeMode === "salons_and_users" && (
                  <div className="flex items-center gap-2">
                    <span>
                      Users access moving to preserved business: {mergePreview?.to_move?.users ?? 0}
                    </span>
                    <InfoTooltip
                      label="Users access moving"
                      text="Users from the source business will be added to the preserved business if missing. Existing roles in the preserved business will not be downgraded."
                    />
                  </div>
                )}
              </div>

              {Array.isArray(mergePreview?.warnings) && mergePreview.warnings.length > 0 && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 space-y-1">
                  <p className="text-sm font-semibold text-rose-700">Warnings</p>
                  <ul className="list-disc pl-5 text-xs text-rose-700 space-y-1">
                    {mergePreview.warnings.map((warning, index) => (
                      <li key={`${warning}-${index}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={mergeAcknowledged}
                onChange={(e) => setMergeAcknowledged(e.target.checked)}
                disabled={isMergingBusinesses}
              />
              <span className="text-sm text-slate-700">
                I understand the LEFT business will be archived and the RIGHT business will be
                preserved.
              </span>
            </label>
          </div>
        )}
      </SBINModal>
      <SBINModal
        isOpen={isAddSalonModalOpen}
        onClose={closeAddSalonModal}
        title="Add a new salon location"
        subtitle="Which business should this salon belong to?"
        size="md"
        fitContent={true}
        closeOnBackdrop={!isCreatingSalon}
        closeOnEscape={!isCreatingSalon}
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeAddSalonModal}
              disabled={isCreatingSalon}
              className={BTN_OUTLINE}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateSalonAndStartSetup}
              disabled={
                isCreatingSalon ||
                (salonBusinessChoice === "same" && !hasCurrentBusiness) ||
                (salonBusinessChoice === "new" && !newBusinessName.trim())
              }
              className={BTN_PRIMARY}
            >
              {isCreatingSalon
                ? "Working..."
                : salonBusinessChoice === "same"
                  ? "Continue"
                  : "Create Business & Continue"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <label className="block rounded-2xl border border-rose-300 bg-rose-50 p-4">
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="salon-business-choice"
                value="same"
                checked={salonBusinessChoice === "same"}
                onChange={() => setSalonBusinessChoice("same")}
                className="mt-1"
                disabled={isCreatingSalon}
              />
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Use my current business (recommended)
                </p>
                <p className="text-xs text-slate-600 mt-1">{currentBusinessLabel}</p>
              </div>
            </div>
          </label>

          <label className="block rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="salon-business-choice"
                value="new"
                checked={salonBusinessChoice === "new"}
                onChange={() => setSalonBusinessChoice("new")}
                className="mt-1"
                disabled={isCreatingSalon}
              />
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Create a new business for this salon
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Use this only if this salon belongs to a different company or you want
                  separate users, billing, and reports.
                </p>
              </div>
            </div>
          </label>

          {salonBusinessChoice === "new" && (
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div>
                <label className={LABEL_TEXT}>
                  Business Name
                </label>
                <input
                  type="text"
                  value={newBusinessName}
                  onChange={(e) => setNewBusinessName(e.target.value)}
                  placeholder="Enter business name"
                  className={`${FIELD_CLASS} w-full`}
                  disabled={isCreatingSalon}
                  required
                />
              </div>
              <div>
                <label className={LABEL_TEXT}>
                  Description (optional)
                </label>
                <textarea
                  value={newBusinessDescription}
                  onChange={(e) => setNewBusinessDescription(e.target.value)}
                  rows={3}
                  className={`${FIELD_CLASS} h-auto min-h-[120px] w-full py-3`}
                  disabled={isCreatingSalon}
                />
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <button
              type="button"
              onClick={() => setShowNewBusinessHelp((prev) => !prev)}
              className="text-sm font-semibold text-slate-700 underline underline-offset-2"
            >
              When should I create a new business?
            </button>
            {showNewBusinessHelp && (
              <ul className="mt-2 list-disc pl-5 text-xs text-slate-600 space-y-1">
                <li>Different owner or legal entity</li>
                <li>Separate staff access</li>
                <li>Separate billing or subscription</li>
                <li>Separate reports</li>
                <li>Otherwise, use your current business.</li>
              </ul>
            )}
          </div>

          {createSalonError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {createSalonError}
            </div>
          )}
        </div>
      </SBINModal>
      <ConfirmDialog
        isOpen={attachSalonConfirmId !== null}
        title="Attach salon to this business?"
        message="This will move the salon into this business. It does not delete data. Proceed?"
        confirmLabel="Attach"
        cancelLabel="Cancel"
        variant="taxExempt"
        onConfirm={handleConfirmAttachSalon}
        onCancel={handleCancelAttachSalon}
      />
      <ConfirmDialog
        isOpen={!!detachSalon}
        title="Detach salon?"
        message={detachMessage}
        confirmLabel="Detach"
        cancelLabel="Cancel"
        variant="taxExempt"
        onConfirm={handleConfirmDetach}
        onCancel={handleCancelDetach}
      />
      <ConfirmModal
        open={!!repairSalon}
        title="Repair Salon Data"
        message={
          repairSalon
            ? `Repair all salon-linked data for "${repairSalon.name}" in this business context?`
            : ""
        }
        confirmText={repairSubmitting ? "Repairing..." : "Repair"}
        cancelText="Cancel"
        onClose={closeRepairModal}
        onConfirm={handleConfirmRepairSalon}
        isBusy={repairSubmitting}
      />
      <SBINModal
        isOpen={detachBlockedModalOpen}
        onClose={() => setDetachBlockedModalOpen(false)}
        title="Cannot Detach Salon"
        size="sm"
        fitContent={true}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setDetachBlockedModalOpen(false)}
            >
              OK
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          You cannot detach the only salon in this business. Add another salon first, or move this
          salon using merge/attach tools.
        </p>
      </SBINModal>
      {flash && (
        <div
          className={`fixed bottom-5 right-5 z-[10040] max-w-md rounded-2xl border px-4 py-3 text-sm shadow-2xl ${
            flash.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-700"
          }`}
        >
          {flash.text}
        </div>
      )}
    </div>
  );
}

